import { StructError } from 'superstruct';
import { Cart } from './schema';
import { Context } from './machine/context';
import { ItemsAddedToCart } from './machine/context/add-items-to-cart/response';
import { updateAdditionalCheckoutButtons } from './machine/action-handlers/update-additional-checkout-buttons';
const { addItemsToCart , changeItemQuantity , fetchCartContents , updateCartNote  } = Context;
const afterUpdate = ()=>{
    void updateAdditionalCheckoutButtons();
};
const animateCartIcon = (originalTotalPrice)=>{
    if (typeof window === 'undefined') {
        return;
    }
    const _cartRect = document.querySelector('.shopcartrect');
    const cartRect = _cartRect instanceof HTMLElement ? _cartRect : null;
    const _cartSqueezed = document.querySelector('.shopcartsqueezed');
    const cartSqueezed = _cartSqueezed instanceof HTMLElement ? _cartSqueezed : null;
    const _cartRectMobile = document.querySelector('.shopcartrectMobile');
    const cartRectMobile = _cartRectMobile instanceof HTMLElement ? _cartRectMobile : null;
    const _cartSqueezedMobile = document.querySelector('.shopcartsqueezedMobile');
    const cartSqueezedMobile = _cartSqueezedMobile instanceof HTMLElement ? _cartSqueezedMobile : null;
    if (cartRect == null || cartRectMobile == null || cartSqueezed == null || cartSqueezedMobile == null) {
        console.error('Cart icon elements not found [b206343f09a449448b6de627e93d749f]');
        return;
    }
    cartRect.style.opacity = '0';
    cartRectMobile.style.opacity = '0';
    cartSqueezed.style.opacity = '1';
    cartSqueezedMobile.style.opacity = '1';
    if (originalTotalPrice === 0) {
        document.querySelectorAll('animate').forEach((element)=>{
            element.beginElement();
        });
    }
    setTimeout(()=>{
        cartRect.style.opacity = '1';
        cartRectMobile.style.opacity = '1';
        cartRect.setAttribute('fill', 'url(#blue-two)');
        cartRectMobile.setAttribute('fill', 'url(#blue-twoMobile)');
        cartSqueezed.style.opacity = '0';
        cartSqueezedMobile.style.opacity = '0';
    }, 150);
};
class CartStore {
    get state() {
        return this._state;
    }
    setState(next) {
        this._state = next;
        const isFailureState = next.name.toLowerCase().includes('failure');
        if (isFailureState) {
            console.warn(`Cart store state changed to ${next.name}`);
        }
        if (next.error) {
            console.error(next.error);
        }
        this._listeners.forEach((cb)=>cb(next));
    }
    subscribe(cb) {
        this._listeners.add(cb);
        return ()=>this._listeners.delete(cb);
    }
    isReady() {
        return this._state.name === 'Ready';
    }
    isRefreshAllowed() {
        return this._state.name === 'Ready' || this._state.name === 'Stale';
    }
    reload() {
        window.location.reload();
    }
    async init() {
        try {
            this.setState({
                name: 'Initializing',
                contents: null,
                error: null
            });
            const rawFetchCartResponse = await fetchCartContents();
            const [err, contents] = Cart.validate(rawFetchCartResponse, {
                coerce: true
            });
            if (err instanceof StructError) {
                console.error('Cart data can not be parsed', err.failures(), contents);
                throw err;
            }
            if (!contents) {
                console.error('Cart data is invalid', contents);
                throw new Error('Cart data is invalid');
            }
            this.setState({
                contents,
                name: 'Ready',
                error: null
            });
        } catch (err) {
            this.setState({
                error: err instanceof Error ? err : new Error('Unknown error [78218467b35c404ab53c58868518bbdf]'),
                name: 'Failure',
                contents: null
            });
        }
    }
    async addItems(payload) {
        const { input , resolve , reject  } = payload;
        if (this._state.contents == null) {
            reject(new Error('Cart contents missing'));
            return;
        }
        try {
            this.setState({
                name: 'AddingItems',
                error: null,
                contents: this._state.contents
            });
            // TODO Side-effects should be part of the context
            animateCartIcon(this._state.contents.original_total_price);
            try {
                const rawAddItemsResponse = await addItemsToCart(input);
                const [err, data] = ItemsAddedToCart.validate(rawAddItemsResponse, {
                    coerce: true
                });
                if (err instanceof StructError) {
                    console.error('Add To Cart response can not be parsed', err.failures(), data);
                }
                resolve(err ? null : data == null ? null : data);
            } catch (err) {
                reject(err instanceof Error ? err : new Error('Unknown error [819a802f80b140d4803315f361ad094b]'));
            }
            const rawFetchCartResponse = await fetchCartContents();
            const [err, contents] = Cart.validate(rawFetchCartResponse, {
                coerce: true
            });
            if (err instanceof StructError) {
                console.error('Cart data can not be parsed', err.failures(), contents);
                throw err;
            }
            if (!contents) {
                console.error('Cart data is invalid', contents);
                throw new Error('Cart data is invalid');
            }
            this.setState({
                contents,
                name: 'Stale',
                error: null
            });
            await this.refresh();
            afterUpdate();
        } catch (err) {
            this.setState({
                error: err instanceof Error ? err : new Error('Unknown error [f84a567a4b1a4ed093eb219f43e3a6df]'),
                name: 'Failure',
                contents: this._state.contents
            });
        }
    }
    async changeQuantity(payload) {
        try {
            if (this._state.contents == null) {
                throw new Error('Cart contents missing');
            }
            this.setState({
                name: 'Updating',
                error: null,
                contents: this._state.contents
            });
            await changeItemQuantity(payload);
            this.setState({
                name: 'Stale',
                error: null,
                contents: this._state.contents
            });
            await this.refresh();
            afterUpdate();
        } catch (err) {
            this.setState({
                error: err instanceof Error ? err : new Error('Unknown error [0fb514aefab3448e8d13bef5134ad7ce]'),
                name: 'Failure',
                contents: this._state.contents
            });
        }
    }
    async removeItems(payload) {
        try {
            const { lineItemKeys  } = payload;
            if (this._state.contents == null) {
                throw new Error('Cart contents missing');
            }
            this.setState({
                name: 'Updating',
                error: null,
                contents: this._state.contents
            });
            await changeItemQuantity({
                items: lineItemKeys.map((lineItemKey)=>({
                        lineItemKey,
                        quantity: 0
                    }))
            });
            this.setState({
                name: 'Stale',
                error: null,
                contents: this._state.contents
            });
            await this.refresh();
        } catch (err) {
            this.setState({
                error: err instanceof Error ? err : new Error('Unknown error [cdc8419e654547b5a21307e0ef1b0b67]'),
                name: 'Failure',
                contents: this._state.contents
            });
        }
    }
    async updateNote(payload) {
        try {
            if (this._state.contents == null) {
                throw new Error('Cart contents missing');
            }
            this.setState({
                name: 'Updating',
                error: null,
                contents: this._state.contents
            });
            try {
                await updateCartNote(payload);
            } catch (err) {
                console.error('Cart note update failed', err);
            }
            this.setState({
                name: 'Stale',
                error: null,
                contents: this._state.contents
            });
            await this.refresh();
        } catch (err) {
            this.setState({
                error: err instanceof Error ? err : new Error('Unknown error [e9a6faf592024eae8f9dae864776906e]'),
                name: 'Failure',
                contents: this._state.contents
            });
        }
    }
    async refresh() {
        try {
            if (this._state.contents == null) {
                throw new Error('Cart contents missing');
            }
            this.setState({
                name: 'Refreshing',
                error: null,
                contents: this._state.contents
            });
            const firstPass = await fetchCartContents();
            const contents = Cart.create(firstPass);
            this.setState({
                contents,
                name: 'Ready',
                error: null
            });
        } catch (err) {
            this.setState({
                error: err instanceof Error ? err : new Error('Unknown error [e9022a2fa11a4051b7b1b4141132a0d8]'),
                name: 'Failure',
                contents: this._state.contents
            });
        }
    }
    constructor(){
        this._state = {
            name: 'Idle',
            contents: null,
            error: null
        };
        this._listeners = new Set();
    }
}
export { CartStore };
export const cartStore = new CartStore();
